import {
  createChart, LineSeries, LineStyle,
  type IChartApi, type ISeriesApi, type MouseEventParams, type Time,
} from "lightweight-charts";
import { toLineData, toSpotData } from "./lineData.js";
import { formatEtTime } from "./timeFormat.js";
import type { LinePoint } from "../types.js";

// Data colours (design §6A — GammaLens canonical).
const CALL = "#3d9bff";
const PUT = "#d4852a";
const CALL_0DTE = "#9d7be8"; // lavender (GammaLens family)
const PUT_0DTE = "#e37ea0"; // rose
const SPOT = "#e8eef8"; // white — the underlying's price overlay

// $M series share the right scale; axis boxes read "858M" (sign via Math.round).
const M_FORMAT = { type: "custom", minMove: 1, formatter: (v: number) => `${Math.round(v)}M` } as const;
const PX_FORMAT = { type: "custom", minMove: 0.01, formatter: (v: number) => v.toFixed(2) } as const;

export interface DeltaChart {
  /** Redraw all series from the full cumulative line (setData each tick). */
  setData(line: LinePoint[]): void;
  /** Show/hide the two dotted 0DTE overlay lines (left-pane toggle). */
  set0dteVisible(visible: boolean): void;
  /**
   * Subscribe to crosshair moves. `timeSec` is the hovered sample's epoch
   * seconds, or null when the pointer leaves the plot. Used by the value strip
   * to rewind its readouts to the hovered moment.
   */
  onHover(cb: (timeSec: number | null) => void): void;
  destroy(): void;
}

export function createDeltaChart(el: HTMLElement): DeltaChart {
  const chart: IChartApi = createChart(el, {
    autoSize: true,
    layout: {
      background: { color: "rgba(0,0,0,0)" }, // let the watermark show through
      textColor: "#3a5880",
    },
    grid: {
      vertLines: { color: "rgba(255,255,255,0.05)" },
      horzLines: { color: "rgba(255,255,255,0.05)" },
    },
    timeScale: {
      timeVisible: true,
      secondsVisible: false,
      tickMarkFormatter: (time: Time) => formatEtTime(time as unknown as number),
    },
    localization: {
      timeFormatter: (time: Time) => formatEtTime(time as unknown as number),
    },
  });

  // Axis-pinned last-value boxes are ON: they are the always-current readout
  // that can never occlude the plot (the old top-right overlay could).
  const call: ISeriesApi<"Line"> = chart.addSeries(LineSeries, {
    color: CALL, lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
    priceFormat: M_FORMAT,
  });
  const put: ISeriesApi<"Line"> = chart.addSeries(LineSeries, {
    color: PUT, lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
    priceFormat: M_FORMAT,
  });
  const call0dte: ISeriesApi<"Line"> = chart.addSeries(LineSeries, {
    color: CALL_0DTE, lineWidth: 2, lineStyle: LineStyle.Dotted, visible: false,
    priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false,
    priceFormat: M_FORMAT,
  });
  const put0dte: ISeriesApi<"Line"> = chart.addSeries(LineSeries, {
    color: PUT_0DTE, lineWidth: 2, lineStyle: LineStyle.Dotted, visible: false,
    priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false,
    priceFormat: M_FORMAT,
  });

  // Underlying price overlay — its own (left) scale: dollars, not $M.
  const spot: ISeriesApi<"Line"> = chart.addSeries(LineSeries, {
    color: SPOT, lineWidth: 1, priceScaleId: "left",
    priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false,
    priceFormat: PX_FORMAT,
  });

  // Zero baseline: a grey solid reference line at value 0 on the call series'
  // price scale (the four $M series share the default right price scale).
  call.createPriceLine({
    price: 0, color: "#4a5670", lineWidth: 1, lineStyle: LineStyle.Solid,
    axisLabelVisible: false,
  });

  return {
    setData(line) {
      call.setData(toLineData(line, "call"));
      put.setData(toLineData(line, "put"));
      call0dte.setData(toLineData(line, "call0dte"));
      put0dte.setData(toLineData(line, "put0dte"));
      const spotData = toSpotData(line);
      spot.setData(spotData);
      // No spot all day (e.g. price feed down) ⇒ hide the empty left axis.
      chart.applyOptions({ leftPriceScale: { visible: spotData.length > 0 } });
    },
    set0dteVisible(visible) {
      call0dte.applyOptions({ visible });
      put0dte.applyOptions({ visible });
    },
    onHover(cb) {
      chart.subscribeCrosshairMove((p: MouseEventParams) => {
        cb(typeof p.time === "number" ? p.time : null);
      });
    },
    destroy() {
      chart.remove();
    },
  };
}
